import { useQuery } from "@tanstack/react-query";

// Shared house-chats query for the host Messages page. Both the "House Chats"
// tab body and the tab-bar unread badge subscribe to this single query key, so
// they share one cache entry / poll (no duplicate network traffic).
export interface HouseChatSummary {
  propertyId: number | null;
  propertyName: string | null;
  address: string | null;
  houseConversationId: number | null;
  lastMessagePreview: string | null;
  lastSenderName: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
}

export const HOUSE_CHATS_KEY = ["/api/app/house-chats"] as const;
const POLL_MS = 10_000;
const EMPTY_POLL_MS = 60_000;

export function useHouseChats() {
  return useQuery<HouseChatSummary[]>({
    queryKey: HOUSE_CHATS_KEY,
    queryFn: async () => {
      const res = await fetch("/api/app/house-chats", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load house chats");
      return res.json();
    },
    // An empty list means no direct properties, not merely no unread messages.
    // Keep a slower discovery poll so properties added elsewhere still appear.
    refetchInterval: (query) =>
      query.state.data?.length === 0 ? EMPTY_POLL_MS : POLL_MS,
    // Override the app's Infinity/false defaults: returning to Messages or
    // refocusing should discover a newly added property without waiting a minute.
    staleTime: POLL_MS,
    refetchOnWindowFocus: true,
  });
}
